import React, { useEffect } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { groupeService } from '../services/api';

const GroupeModal = ({ show, onHide, groupe }) => {
  const queryClient = useQueryClient();
  const isEditing = !!groupe;

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      nom: '',
      description: '',
      couleurHex: '#0d6efd'
    }
  });

  const couleurSelectionnee = watch('couleurHex');

  // Mutations
  const createMutation = useMutation(groupeService.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('groupes');
      onHide();
      reset();
    }
  });

  const updateMutation = useMutation(
    ({ id, data }) => groupeService.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('groupes');
        onHide();
      }
    }
  );

  // Effet pour réinitialiser le formulaire quand le groupe change
  useEffect(() => {
    if (groupe) {
      reset({
        nom: groupe.nom || '',
        description: groupe.description || '',
        couleurHex: groupe.couleurHex || '#0d6efd'
      });
    } else {
      reset({
        nom: '',
        description: '',
        couleurHex: '#0d6efd'
      });
    }
  }, [groupe, reset]);

  const onSubmit = async (data) => {
    try {
      if (isEditing) {
        await updateMutation.mutateAsync({ id: groupe.id, data });
      } else {
        await createMutation.mutateAsync(data);
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleClose = () => {
    onHide();
    reset();
  };

  const mutation = isEditing ? updateMutation : createMutation;

  // Couleurs prédéfinies populaires
  const couleursPredefinies = [
    '#0d6efd', // Bleu Bootstrap
    '#198754', // Vert Bootstrap
    '#dc3545', // Rouge Bootstrap
    '#ffc107', // Jaune Bootstrap
    '#fd7e14', // Orange Bootstrap
    '#6f42c1', // Violet Bootstrap
    '#20c997', // Teal Bootstrap
    '#e91e63', // Rose
    '#9c27b0', // Violet foncé
    '#673ab7', // Deep Purple
    '#3f51b5', // Indigo
    '#2196f3', // Bleu
    '#03a9f4', // Light Blue
    '#00bcd4', // Cyan
    '#009688', // Teal foncé
    '#4caf50', // Vert clair
    '#8bc34a', // Light Green
    '#cddc39', // Lime
    '#ffeb3b', // Jaune clair
    '#ff9800', // Orange
    '#ff5722', // Deep Orange
    '#795548', // Marron
    '#607d8b'  // Blue Grey
  ];

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isEditing ? 'Modifier le groupe' : 'Nouveau groupe'}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          {mutation.error && (
            <Alert variant="danger">
              Erreur: {mutation.error.response?.data || mutation.error.message}
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Nom du groupe *</Form.Label>
            <Form.Control
              type="text"
              {...register('nom', { 
                required: 'Le nom du groupe est obligatoire',
                minLength: { value: 2, message: 'Le nom doit faire au moins 2 caractères' },
                maxLength: { value: 50, message: 'Le nom ne peut pas dépasser 50 caractères' }
              })}
              isInvalid={!!errors.nom}
              placeholder="Ex: Équipe Développement, Service Comptabilité..."
            />
            <Form.Control.Feedback type="invalid">
              {errors.nom?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              {...register('description', {
                maxLength: { value: 200, message: 'La description ne peut pas dépasser 200 caractères' }
              })}
              isInvalid={!!errors.description}
              placeholder="Description optionnelle du groupe..."
            />
            <Form.Control.Feedback type="invalid">
              {errors.description?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Couleur du groupe</Form.Label>
            <div className="d-flex align-items-center mb-2">
              <Form.Control
                type="color"
                {...register('couleurHex')}
                style={{ width: '60px', height: '40px', marginRight: '10px' }}
              />
              <div
                style={{
                  width: '100px',
                  height: '40px',
                  backgroundColor: couleurSelectionnee,
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}
              >
                Aperçu
              </div>
              <Form.Control
                type="text"
                {...register('couleurHex', {
                  pattern: {
                    value: /^#[0-9A-Fa-f]{6}$/,
                    message: 'Format de couleur invalide (utilisez #RRGGBB)'
                  }
                })}
                isInvalid={!!errors.couleurHex}
                placeholder="#0d6efd"
                className="ms-2"
                style={{ maxWidth: '100px' }}
              />
            </div>
            <Form.Control.Feedback type="invalid">
              {errors.couleurHex?.message}
            </Form.Control.Feedback>
            
            {/* Couleurs prédéfinies */}
            <div className="mt-2">
              <small className="text-muted">Couleurs prédéfinies:</small>
              <div className="d-flex flex-wrap gap-1 mt-1">
                {couleursPredefinies.map(couleur => (
                  <button
                    key={couleur}
                    type="button"
                    style={{
                      width: '30px',
                      height: '30px',
                      backgroundColor: couleur,
                      border: couleurSelectionnee === couleur ? '3px solid #000' : '1px solid #dee2e6',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                    onClick={() => reset(prev => ({ ...prev, couleurHex: couleur }))}
                    title={couleur}
                  />
                ))}
              </div>
            </div>
          </Form.Group>

          {isEditing && groupe && (
            <Alert variant="info">
              <strong>Informations:</strong> Ce groupe contient actuellement {groupe.nombreAgents || 0} agent{(groupe.nombreAgents || 0) !== 1 ? 's' : ''}.
            </Alert>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Annuler
          </Button>
          <Button 
            variant="primary" 
            type="submit"
            disabled={mutation.isLoading}
          >
            {mutation.isLoading ? 'Sauvegarde...' : (isEditing ? 'Modifier' : 'Créer')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default GroupeModal; 